import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { AuthUserButton } from '@/lib/auth'
import { filterNavGroups, type EnabledScreens } from '@/lib/nav-config'
import { enabledScreens } from '@/lib/runtime-config'

interface LayoutProps {
  children: React.ReactNode
  /**
   * WU-07: capability × deploy 設定を合成した実効画面セット。
   * 未指定時は WU-04 の deploy 単位 enabledScreens のみ（従来挙動）。
   */
  screens?: EnabledScreens
}

export function Layout({ children, screens }: LayoutProps) {
  const location = useLocation()
  const effectiveScreens = screens !== undefined ? screens : enabledScreens()

  return (
    <div className="flex min-h-[100dvh] w-full max-w-full flex-col bg-background md:h-[100dvh] md:flex-row">
      {/* Sidebar */}
      <aside
        data-app-chrome
        className="w-full min-w-0 max-w-full shrink-0 border-b bg-card md:w-64 md:border-b-0 md:border-r"
      >
        <div className="flex h-14 items-center border-b px-4 md:h-16 md:px-6">
          <h1 className="text-xl font-bold text-primary">Context Harness</h1>
        </div>
        <nav aria-label="主要ナビゲーション" className="min-w-0 max-w-full overflow-x-auto p-2 md:p-4">
          <div className="flex w-max gap-4 md:block md:w-auto md:space-y-6">
            {filterNavGroups(effectiveScreens).map((group) => (
              <div key={group.label} className="flex shrink-0 items-center gap-1 md:block md:space-y-1">
                <p className="sr-only px-3 text-xs font-semibold text-muted-foreground md:not-sr-only">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.screen}
                      to={item.href}
                      className={cn(
                        'flex min-h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:gap-3',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.name}
                    </Link>
                  )
                })}
              </div>
            ))}
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <header
          data-app-chrome
          className="flex h-14 shrink-0 items-center justify-end border-b px-4 md:h-16 md:px-6"
        >
          <AuthUserButton />
        </header>

        {/* Page content */}
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
